import { cn } from "lib/utils";
import { ArrowIcon, CheckCircleIcon } from "asset/icons";
import { forwardRef, isValidElement, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const optionBaseClassName =
    "aku-body-normal-medium py-[12px] px-[16px] cursor-pointer flex items-center gap-[16px] text-main rounded-lg transition-colors duration-200 hover:bg-[#F8F7FF]";

const selectedOptionClassName = "bg-[#F4F2FC]";

const Dropdown = forwardRef(
    (
        {
            value,
            options,
            onChange,
            className = "",
            label = "",
            labelClassName = "",
            placeholder = "",
            dropdownClassName = "",
            required = true,
            readOnly = false,
            inputBg = "#f1f5f9",
            searchable = false,
            searchPlaceholder = "Search",
            error,
        },
        ref
    ) => {
        const [show, setShow] = useState(false);
        const [searchTerm, setSearchTerm] = useState("");
        const [menuStyle, setMenuStyle] = useState({});
        const dropdownRef = useRef(null);
        const menuRef = useRef(null);
        const selectedOption = options.find((option) => option.value === value);
        const selectedValue = (val) => value === val;
        const filteredOptions =
            searchable && searchTerm.trim()
                ? options.filter((option) => {
                      const searchableText = String(
                          option.searchLabel ||
                              option.name ||
                              option.title ||
                              option.displayValue ||
                              (!isValidElement(option.label) ? option.label : "") ||
                              option.value ||
                              ""
                      ).toLowerCase();

                      return searchableText.includes(searchTerm.trim().toLowerCase());
                  })
                : options;

        const updateDropdownPosition = useCallback(() => {
            if (!dropdownRef.current) return;

            const rect = dropdownRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const viewportPadding = 16;
            const menuGap = 8;
            const menuWidth = Math.min(
                Math.max(rect.width, 270),
                viewportWidth - viewportPadding * 2
            );
            const left = Math.min(
                Math.max(rect.left, viewportPadding),
                viewportWidth - menuWidth - viewportPadding
            );
            const desiredHeight = Math.min(Math.max(options.length * 48 + 16, 120), 280);
            const spaceBelow = viewportHeight - rect.bottom - viewportPadding - menuGap;
            const spaceAbove = rect.top - viewportPadding - menuGap;
            const shouldOpenUpward = spaceBelow < desiredHeight && spaceAbove > spaceBelow;
            const availableHeight = Math.max(shouldOpenUpward ? spaceAbove : spaceBelow, 80);
            const maxHeight = Math.min(desiredHeight, availableHeight);

            setMenuStyle({
                left,
                maxHeight,
                top: shouldOpenUpward
                    ? Math.max(viewportPadding, rect.top - menuGap - maxHeight)
                    : Math.min(rect.bottom + menuGap, viewportHeight - viewportPadding),
                width: menuWidth,
            });
        }, [options.length]);

        // Effect to close dropdown on outside click
        useEffect(() => {
            const handleOutsideClick = (event) => {
                const clickedTrigger = dropdownRef.current?.contains(event.target);
                const clickedMenu = menuRef.current?.contains(event.target);

                if (!clickedTrigger && !clickedMenu) {
                    setShow(false);
                }
            };

            if (show) {
                document.addEventListener("mousedown", handleOutsideClick);
            }

            return () => {
                document.removeEventListener("mousedown", handleOutsideClick);
            };
        }, [show]);

        useEffect(() => {
            if (!show) return;

            setSearchTerm("");
            updateDropdownPosition();
            window.addEventListener("resize", updateDropdownPosition);
            window.addEventListener("scroll", updateDropdownPosition, true);

            return () => {
                window.removeEventListener("resize", updateDropdownPosition);
                window.removeEventListener("scroll", updateDropdownPosition, true);
            };
        }, [show, options.length, updateDropdownPosition]);

        const menu =
            show &&
            createPortal(
                <div
                    ref={menuRef}
                    style={menuStyle}
                    className={cn(
                        "fixed z-[10000] max-h-[min(280px,calc(100vh-32px))] overflow-y-auto !border !border-[#E6E8F9] rounded-xl bg-white shadow-[-16px_-16px_16px_-4px_#10182808,16px_16px_32px_-4px_#10182814]",
                        dropdownClassName
                    )}
                    onClick={(event) => event.stopPropagation()}
                >
                    {searchable && (
                        <div className="sticky top-0 z-10 bg-white p-2">
                            <input
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder={searchPlaceholder}
                                className="aku-body-normal-regular h-12 w-full rounded-lg bg-[#f1f5f9] px-4 text-main-dark placeholder:text-main-grey outline-none"
                                autoFocus
                            />
                        </div>
                    )}
                    {filteredOptions.map(({ label, value, disabled, action, className }, index) => {
                        const selected = selectedValue(value);

                        return (
                            <p
                                className={cn(
                                    optionBaseClassName,
                                    selected && selectedOptionClassName,
                                    disabled && "pointer-events-none !text-[#BCC2CC]",
                                    className
                                )}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setShow(false);
                                    onChange ? onChange(value) : action();
                                }}
                                key={value + index}
                            >
                                {isValidElement(label) ? label : <span>{label}</span>}
                                {selected && <CheckCircleIcon className="ml-auto shrink-0" />}
                            </p>
                        );
                    })}
                    {filteredOptions.length === 0 && (
                        <p className="aku-body-normal-medium px-4 py-3 text-main-grey">
                            No results found
                        </p>
                    )}
                </div>,
                document.body
            );

        return (
            <>
                {label && (
                    <p className={cn("mb-2 font-medium text-sm text-main-dark", labelClassName)}>
                        {label}
                    </p>
                )}
                <div
                    ref={dropdownRef}
                    className={cn(
                        `cursor-pointer relative flex w-full items-center justify-between rounded-lg !pr-4 h-[56px]`,
                        error && !readOnly && "!bg-[#FFF1F3] ring-1 ring-[#F04438]",
                        className
                    )}
                    style={{
                        backgroundColor: readOnly ? "#F8FAFC" : error ? "#FFF1F3" : inputBg,
                    }}
                    onClick={() => !readOnly && setShow((prev) => !prev)}
                    aria-disabled={readOnly}
                >
                    <input
                        required={required}
                        className={cn(
                            "aku-body-normal-regular w-full !pl-4 bg-transparent text-main-dark placeholder:text-main-grey pointer-events-none truncate",
                            readOnly && "!text-[#98A2B3] [-webkit-text-fill-color:#98A2B3]",
                        )}
                        style={{
                            boxShadow: `inset 0 0 0 1000px ${
                                readOnly ? "#F8FAFC" : error ? "#FFF1F3" : inputBg
                            }`,
                        }}
                        placeholder={placeholder}
                        value={
                            selectedOption?.displayValue ||
                            selectedOption?.title ||
                            (!isValidElement(selectedOption?.label) ? selectedOption?.label : "") ||
                            value
                        }
                        readOnly
                    />
                    <ArrowIcon className="*:stroke-main-grey ml-2 size-3.5" />
                </div>
                {error && <p className="mt-1 text-xs text-[#D92D20]">{error}</p>}
                {menu}
            </>
        );
    }
);

export default Dropdown;
